import type { PostgrestError } from "@supabase/supabase-js";
import { createEntityImageSignedUrl, type EntityRelationshipRecord } from "@/features/entities";
import { mapEntityCardRecordToView } from "@/features/entities/mappers/entityMappers";
import { createLoreCoverSignedUrl } from "@/features/lore/api/loreStorage";
import { mapLoreEntryRecordToView } from "@/features/lore/mappers/loreMappers";
import { buildAffinityChartSnapshot } from "@/features/affinity-chart/affinity-chart.repository";
import { isSupabaseConfigured } from "@/lib/env";
import type { Database } from "@/lib/database.types";
import { supabase } from "@/lib/supabase";
import type { EntityCardRecord } from "@/types/entity";
import type { LoreEntryRecord } from "@/types/lore";
import type { SubscriptionTier } from "@/types/projects";
import type {
  AffinityChartLayoutPositions,
  AffinityChartSnapshot,
} from "@/features/affinity-chart/affinity-chart.types";

export type AffinityChartActionErrorCode =
  | "generic"
  | "missing-config"
  | "access-denied"
  | "not-found"
  | "tier-required";

type TierPrompt = {
  recommendedTier: SubscriptionTier;
  tier: SubscriptionTier;
};

interface AffinityChartActionErrorOptions {
  cause?: unknown;
  tierPrompt?: TierPrompt;
}

export class AffinityChartActionError extends Error {
  readonly cause?: unknown;
  readonly tierPrompt?: TierPrompt;

  constructor(
    public readonly code: AffinityChartActionErrorCode,
    message?: string,
    options?: AffinityChartActionErrorOptions,
  ) {
    super(message ?? code);
    this.name = "AffinityChartActionError";
    this.cause = options?.cause;
    this.tierPrompt = options?.tierPrompt;
  }
}

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new AffinityChartActionError(
      "missing-config",
      "Supabase environment variables are not configured.",
    );
  }
}

async function getCurrentUserId() {
  ensureConfigured();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new AffinityChartActionError("access-denied", "No authenticated user available.");
  }

  return user.id;
}

function parseTierRequiredError(message: string) {
  const [prefix, tier, recommendedTier] = message.split("|");

  if (prefix !== "AFFINITY_CHART_TIER_REQUIRED" || !tier || !recommendedTier) {
    return null;
  }

  return {
    recommendedTier: recommendedTier as SubscriptionTier,
    tier: tier as SubscriptionTier,
  };
}

function mapAffinityChartError(error: unknown) {
  if (error instanceof AffinityChartActionError) {
    return error;
  }

  const message =
    typeof error === "object" && error && "message" in error
      ? String(error.message)
      : "Affinity Chart action failed.";

  const tierPrompt = parseTierRequiredError(message);
  if (tierPrompt) {
    return new AffinityChartActionError("tier-required", message, {
      cause: error,
      tierPrompt,
    });
  }

  const postgrestError = error as PostgrestError | undefined;
  if (postgrestError?.code === "PGRST116") {
    return new AffinityChartActionError("not-found", "That graph record is unavailable.", {
      cause: error,
    });
  }

  if (
    postgrestError?.code === "42501"
    || message === "PROJECT_ACCESS_DENIED"
    || message.toLowerCase().includes("row-level security")
  ) {
    return new AffinityChartActionError("access-denied", "You do not have access to that graph.", {
      cause: error,
    });
  }

  return new AffinityChartActionError("generic", message, { cause: error });
}

async function resolveEntityImageUrls(rows: EntityCardRecord[]) {
  const pairs = await Promise.all(
    rows.map(async (row) => {
      if (!row.image_path) {
        return [row.id, null] as const;
      }

      try {
        return [row.id, await createEntityImageSignedUrl(row.image_path)] as const;
      } catch {
        return [row.id, null] as const;
      }
    }),
  );

  return new Map(pairs);
}

async function resolveLoreCoverUrls(rows: LoreEntryRecord[]) {
  const pairs = await Promise.all(
    rows.map(async (row) => {
      if (!row.cover_image_path) {
        return [row.id, null] as const;
      }

      try {
        return [row.id, await createLoreCoverSignedUrl(row.cover_image_path)] as const;
      } catch {
        return [row.id, null] as const;
      }
    }),
  );

  return new Map(pairs);
}

function sanitizeWritePositions(positions: AffinityChartLayoutPositions) {
  return Object.fromEntries(
    Object.entries(positions).filter(([, value]) =>
      Number.isFinite(value.x) && Number.isFinite(value.y),
    ),
  );
}

export function formatAffinityChartErrorMessage(error: unknown) {
  const affinityChartError = mapAffinityChartError(error);

  switch (affinityChartError.code) {
    case "missing-config":
      return "affinityChart.messages.missingConfig";
    case "access-denied":
      return "affinityChart.messages.accessDenied";
    case "not-found":
      return "affinityChart.messages.notFound";
    case "tier-required":
      return "affinityChart.messages.tierRequired";
    default:
      return "affinityChart.messages.loadError";
  }
}

export function getAffinityChartTierPrompt(error: unknown) {
  return mapAffinityChartError(error).tierPrompt ?? null;
}

export async function loadAffinityChartSnapshot(projectId: string): Promise<AffinityChartSnapshot> {
  ensureConfigured();

  const [entityResult, relationshipResult, loreResult, layoutResult] = await Promise.all([
    supabase.from("entity_cards").select("*").eq("project_id", projectId),
    supabase.from("relationships").select("*").eq("project_id", projectId),
    supabase.from("lore_entries").select("*").eq("project_id", projectId),
    supabase.from("graph_layouts").select("positions").eq("project_id", projectId).maybeSingle(),
  ]);

  if (entityResult.error) {
    throw mapAffinityChartError(entityResult.error);
  }

  if (relationshipResult.error) {
    throw mapAffinityChartError(relationshipResult.error);
  }

  if (loreResult.error) {
    throw mapAffinityChartError(loreResult.error);
  }

  if (layoutResult.error) {
    throw mapAffinityChartError(layoutResult.error);
  }

  const entityRows = (entityResult.data as EntityCardRecord[] | null) ?? [];
  const relationshipRows = (relationshipResult.data as EntityRelationshipRecord[] | null) ?? [];
  const loreRows = (loreResult.data as LoreEntryRecord[] | null) ?? [];
  const [entityImageUrls, loreCoverUrls] = await Promise.all([
    resolveEntityImageUrls(entityRows),
    resolveLoreCoverUrls(loreRows),
  ]);

  const loreEntriesById = new Map(
    loreRows.map((row) => [
      row.id,
      {
        canon_status: row.canon_status,
        id: row.id,
        title: row.title,
        type: row.type,
      },
    ]),
  );
  const entityRowsById = new Map(entityRows.map((row) => [row.id, row]));

  const entityCards = entityRows.map((row) =>
    mapEntityCardRecordToView(row, {
      imageUrl: entityImageUrls.get(row.id) ?? null,
      loreEntriesById,
      projectRelationships: relationshipRows,
      projectRowsById: entityRowsById,
    }),
  );
  const loreEntries = loreRows.map((row) =>
    mapLoreEntryRecordToView(row, loreCoverUrls.get(row.id) ?? null),
  );

  return buildAffinityChartSnapshot({
    entityCards,
    layoutPositions: layoutResult.data?.positions ?? {},
    loreEntries,
    projectId,
    relationships: relationshipRows,
  });
}

export async function saveAffinityChartLayout(
  projectId: string,
  positions: AffinityChartLayoutPositions,
) {
  ensureConfigured();

  const userId = await getCurrentUserId();
  const nextPositions = sanitizeWritePositions(positions);

  const { data, error } = await supabase
    .from("graph_layouts")
    .upsert(
      {
        positions: nextPositions as unknown as Database["public"]["Tables"]["graph_layouts"]["Insert"]["positions"],
        project_id: projectId,
        user_id: userId,
      },
      { onConflict: "project_id,user_id" },
    )
    .select("positions")
    .single();

  if (error) {
    throw mapAffinityChartError(error);
  }

  return data?.positions ?? nextPositions;
}

export async function deleteAffinityChartLayout(projectId: string) {
  ensureConfigured();

  const userId = await getCurrentUserId();
  const { error } = await supabase
    .from("graph_layouts")
    .delete()
    .eq("project_id", projectId)
    .eq("user_id", userId);

  if (error) {
    throw mapAffinityChartError(error);
  }
}
